/**
 * BnthSlidePanel
 *
 * Generic base custom element for slide-in/dropdown panels: open/close/
 * toggle state, trigger resolution (an internal [data-trigger], or an
 * external trigger living outside the element via data-trigger-selector,
 * for panels that can't nest their trigger inside the element itself),
 * a [data-close] button, Escape key, and outside-click dismissal.
 *
 * Dispatches "slidepanel:open"/"slidepanel:close" so consumers can react
 * (focus an input, reset state) without this base needing domain
 * knowledge. Panels that need extra behaviour (e.g. BnthCountrySelector's
 * radio/apply handling) should extend this class; panels that need
 * nothing extra can use the <bn3th-slide-panel> tag directly.
 */
class BnthSlidePanel extends HTMLElement {
  connectedCallback() {
    this._trigger = this.querySelector("[data-trigger]") || this._resolveExternalTrigger();
    this._close = this.querySelector("[data-close]");

    this._boundOutsideClick = this._onOutsideClick.bind(this);
    this._boundKeyDown = this._onKeyDown.bind(this);

    this._trigger?.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggle();
    });

    this._close?.addEventListener("click", () => this.close());

    document.addEventListener("click", this._boundOutsideClick);
    document.addEventListener("keydown", this._boundKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._boundOutsideClick);
    document.removeEventListener("keydown", this._boundKeyDown);
  }

  open() {
    this.setAttribute("data-open", "");
    this.setAttribute("aria-hidden", "false");
    this._trigger?.setAttribute("aria-expanded", "true");
    this.dispatchEvent(new CustomEvent("slidepanel:open"));
  }

  close() {
    this.removeAttribute("data-open");
    this.setAttribute("aria-hidden", "true");
    this._trigger?.setAttribute("aria-expanded", "false");
    this.dispatchEvent(new CustomEvent("slidepanel:close"));
  }

  toggle() {
    this.hasAttribute("data-open") ? this.close() : this.open();
  }

  _resolveExternalTrigger() {
    return this.dataset.triggerSelector ? document.querySelector(this.dataset.triggerSelector) : null;
  }

  _onOutsideClick(event) {
    if (this.contains(event.target) || this._trigger?.contains(event.target)) return;
    this.close();
  }

  _onKeyDown(event) {
    if (event.key === "Escape") this.close();
  }
}

customElements.define("bn3th-slide-panel", BnthSlidePanel);
